<script lang="ts">
    import {afterNavigate, goto} from '$app/navigation';
    import {page} from '$app/stores';
    import Breadcrumbs from '$lib/components/Breadcrumbs.svelte';
    import Pagination from '$lib/components/Pagination.svelte';
    import SupplierCatalogItem from '$lib/components/SupplierCatalogItem.svelte';
    import {normalizeDomain} from '$lib/utils';
    import {tooltip} from 'svooltip';
    import type {PageData} from './$types';
    import SupplierDetail from './SupplierDetail.svelte';

    export let data: PageData;

    let search_query = $page.url.searchParams.get('q') || '';
    let search_free = $page.url.searchParams.get('free') === '1';
    let search_city = data.city?.slug ?? '';

    afterNavigate(() => {
        search_query = $page.url.searchParams.get('q') || '';
        search_free = $page.url.searchParams.get('free') === '1';
        search_city = data.city?.slug ?? '';
    });

    $: domainQuery = normalizeDomain(search_query);

    function onUpdateSearchForm() {
        const u = new URL('/catalog', window.location.href);

        if (search_city) {
            u.pathname += '/' + search_city;
        }

        if (data.category) {
            u.pathname += '/' + data.category.slug;
        }

        if (search_query) {
            u.searchParams.set('q', search_query);
        }

        if (search_free) {
            u.searchParams.set('free', '1');
        }

        goto(u.toString(), {
            replaceState: true,
            keepFocus: true,
            noScroll: true,
        });
    }
</script>

<svelte:head>
    <title>{data.meta.title}</title>
    <meta name="description" content={data.meta.description} />
    <link href={$page.url.href.split('?')[0]} rel="canonical" />
</svelte:head>

{#if data.type === 'catalog'}
    <div class="container p-4">
        <div class="flex flex-col items-start max-w-5xl gap-8 mx-auto lg:flex-row">
            <div class="w-full max-w-3xl">
                <div class="mb-4">
                    <Breadcrumbs
                        list={[
                            {title: 'Каталог сайтов', url: '/catalog'},
                            ...(data.city ? [{title: data.city.title, url: `/catalog/${data.city.slug}`}] : []),
                            ...(!data.city && data.category ? [{title: data.category.title, url: `/catalog/${data.category.slug}`}] : []),
                            ...(data.city && data.category
                                ? [
                                      {
                                          title: data.category.title,
                                          url: `/catalog/${data.city.slug}/${data.category.slug}`,
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </div>

                <h1 class="page-title">
                    Каталог сайтов для парсинга Q-Parser
                    {#if data.city}
                        — {data.city.title}{/if}
                    {#if data.category}
                        — {data.category.title}{/if}
                </h1>

                <form class="flex flex-row items-stretch justify-start w-full h-10 max-w-xl mb-3 md:h-12 flex-nowrap">
                    <div class="relative w-full h-full grow shrink">
                        <input
                            class="box-border w-full h-full pl-4 pr-12 border rounded-l border-gray focus:bg-green-50"
                            placeholder="Поиск по каталогу..."
                            type="text"
                            name="q"
                            bind:value={search_query}
                            on:input={onUpdateSearchForm}
                        />

                        {#if search_query}
                            <button
                                type="button"
                                class="absolute w-4 h-4 transition-transform rotate-0 -translate-y-1/2 cursor-pointer right-4 top-1/2 hover:rotate-90"
                                on:click={() => ((search_query = ''), onUpdateSearchForm())}
                                title="Очистить запрос"
                                use:tooltip
                            >
                                <svg class="w-full h-full fill-red" viewBox="0 0 357 357" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M357 35.7L321.3 0 178.5 142.8 35.7 0 0 35.7l142.8 142.8L0 321.3 35.7 357l142.8-142.8L321.3 357l35.7-35.7-142.8-142.8z"
                                    />
                                </svg>
                            </button>
                        {/if}
                    </div>

                    <div class="grow-0 shrink-0 w-36 md:w-44 lg:w-52">
                        <select
                            class="box-border w-full h-full px-4 border-r rounded-r border-y border-gray"
                            bind:value={search_city}
                            on:change={onUpdateSearchForm}
                        >
                            <option value="">Все города</option>
                            {#each data.cities as city}
                                <option value={city.slug}>{city.title}</option>
                            {/each}
                        </select>
                    </div>
                </form>

                <div class="flex flex-row flex-wrap items-stretch justify-start w-full max-w-xl mb-3 text-sm gap-x-6 gap-y-1">
                    <button
                        type="button"
                        class="flex items-center py-2 cursor-pointer group gap-x-3 text-black-500 hover:text-green"
                        on:click={() => ((search_free = !search_free), onUpdateSearchForm())}
                    >
                        {#if search_free}
                            <div class="w-2 h-2 rounded-sm bg-black-800 ring-1 ring-gray-300 ring-offset-2" />
                        {:else}
                            <div class="w-2 h-2 bg-white rounded-sm group-hover:bg-gray ring-1 ring-gray ring-offset-2" />
                        {/if}
                        Только бесплатные
                    </button>
                </div>

                {#if data.suppliers.length > 0}
                    <div class="divide-y divide-gray">
                        {#each data.suppliers as supplier (supplier.id)}
                            <SupplierCatalogItem {supplier} />
                        {/each}

                        {#if data.pagination.current.last}
                            <div class="max-w-full py-6 mx-auto prose text-center">
                                <p>Не нашли то, что искали?</p>
                                <p>
                                    <a class="btn btn-green" href="/catalog/add">Добавить новый сайт</a>
                                </p>
                            </div>
                        {/if}
                    </div>
                    <Pagination pagination={data.pagination} />
                {:else}
                    <div class="py-6 mx-auto prose text-center">
                        {#if domainQuery}
                            <p>
                                Сайта <b>{domainQuery}</b> нет в нашем каталоге.<br />Отправьте заявку на настройку уже сегодня!
                            </p>
                        {:else}
                            <p>
                                По вашему запросу ничего не найдено. Попробуйте упростить поисковый запрос или выбрать другой город или
                                категорию.
                            </p>
                        {/if}

                        <p>
                            <a class="btn btn-green" href="/catalog/add">Добавить новый сайт</a>
                        </p>
                    </div>
                {/if}
            </div>

            <nav class="lg:max-w-sm lg:sticky top-4 lg:w-80 shrink-0">
                <div class="page-title lg:hidden col-span-full">Категории сайтов</div>

                <ul class="flex flex-row flex-wrap rounded lg:flex-col gap-y-2 gap-x-8 lg:border lg:p-4 lg:border-gray">
                    <li>
                        <a class="text-black" class:font-bold={!data.category} href="/catalog{data.city ? `/${data.city.slug}` : ''}"
                            >Все категории</a
                        >
                    </li>

                    {#each data.categories as category}
                        <li>
                            <a
                                class="text-black"
                                class:font-bold={category.slug === data?.category?.slug}
                                href="/catalog{data.city ? `/${data.city.slug}` : ''}/{category.slug}">{category.title}</a
                            >
                        </li>
                    {/each}
                </ul>
            </nav>
        </div>
    </div>
{:else if data.type === 'detail'}
    <SupplierDetail supplier={data.supplier} />
{/if}
